import { XIcon } from "lucide-react";

import type { WorkspaceFileWriteState } from "~/hooks/useWorkspaceFileWrite";
import { Button } from "./ui/button";

export function workspaceFileTransferPercent(state: WorkspaceFileWriteState): number {
  if (state.status !== "saving") return 0;
  return state.totalBytes === 0 ? 100 : Math.round((state.completedBytes / state.totalBytes) * 100);
}

interface TransferProgressBarProps {
  readonly percent: number;
}

function TransferProgressBar({ percent }: TransferProgressBarProps) {
  return (
    <div
      className="h-1.5 min-w-0 flex-1 overflow-hidden rounded-full bg-muted"
      role="progressbar"
      aria-label="Project file transfer progress"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={percent}
    >
      <div
        className="h-full bg-primary transition-[width] duration-150"
        style={{ width: `${percent}%` }}
      />
    </div>
  );
}

interface CompactWorkspaceFileTransferStatusProps {
  readonly state: WorkspaceFileWriteState;
  readonly onCancel: () => void;
}

export function CompactWorkspaceFileTransferStatus({
  state,
  onCancel,
}: CompactWorkspaceFileTransferStatusProps) {
  if (state.status !== "saving") return null;
  const percent = workspaceFileTransferPercent(state);
  return (
    <div className="flex h-8 shrink-0 items-center gap-2 border-b border-border/40 px-3">
      <TransferProgressBar percent={percent} />
      <span className="w-8 text-right text-[11px] tabular-nums text-muted-foreground">
        {percent}%
      </span>
      <Button
        size="icon-xs"
        variant="ghost"
        onClick={onCancel}
        aria-label="Cancel project save"
        title="Cancel save"
      >
        <XIcon className="size-3.5" />
      </Button>
    </div>
  );
}

interface DialogWorkspaceFileTransferStatusProps {
  readonly state: WorkspaceFileWriteState;
}

export function DialogWorkspaceFileTransferStatus({
  state,
}: DialogWorkspaceFileTransferStatusProps) {
  if (state.status === "saving") {
    const percent = workspaceFileTransferPercent(state);
    return (
      <div className="grid gap-1.5" role="status" aria-live="polite">
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span>Transferring file</span>
          <span>{percent}%</span>
        </div>
        <TransferProgressBar percent={percent} />
      </div>
    );
  }
  if (state.status === "error") {
    return (
      <p className="text-xs text-destructive" role="alert">
        {state.message}
      </p>
    );
  }
  return state.status === "cancelled" ? (
    <p className="text-xs text-muted-foreground" role="status">
      Save cancelled. The incomplete transfer was discarded.
    </p>
  ) : null;
}
